import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { useCompany } from "../../contexts/CompanyContext";
import { useRole } from "../../contexts/RoleContext";

interface HubStat { hub: string; count: number; }
interface WeeklyReport { week_label: string; status: string; }
interface FinalPresentation { title: string; admin_score: number | null; admin_feedback: string | null; status: string; }

const HUB_LABELS: Record<string, string> = {
  AR: "아티스트 관리", Marketing: "마케팅", Finance: "재무", Legal: "법무",
  Concert: "공연기획", Foundation: "문화재단", IP: "IP사업", Campaign: "캠페인",
};
const HUB_COLORS = ["#E8375F","#FF9F43","#00C896","#7A9BFF","#A78BFA","#EAB308","#EC4899","#14B8A6"];

function getRankName(rep: number) {
  if (rep >= 81) return "부장(팀장)"; if (rep >= 61) return "과장"; if (rep >= 41) return "대리"; if (rep >= 21) return "사원"; return "인턴";
}
function getRankColor(rep: number) {
  if (rep >= 81) return "#EAB308"; if (rep >= 61) return "#E8375F"; if (rep >= 41) return "#FF9F43"; if (rep >= 21) return "#7A9BFF"; return "#888888";
}

export default function PortfolioPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { companyState, workspaceId } = useCompany();
  const { jobRole, roleConfig, companyConfig } = useRole();

  const [hubStats, setHubStats] = useState<HubStat[]>([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [weeklyReports, setWeeklyReports] = useState<WeeklyReport[]>([]);
  const [approvalCount, setApprovalCount] = useState(0);
  const [eventCount, setEventCount] = useState(0);
  const [streakDays, setStreakDays] = useState(0);
  const [totalCheckins, setTotalCheckins] = useState(0);
  const [finalPresentation, setFinalPresentation] = useState<FinalPresentation | null>(null);
  const [loading, setLoading] = useState(true);
  const [joinedAt, setJoinedAt] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!user || !workspaceId) return;
    setLoading(true);
    const [{ data: tasks }, { data: reports }, { data: approvals }, { data: events }, { data: checkins }, { data: fp }, { data: member }] = await Promise.all([
      supabase.from("task_completions").select("hub").eq("workspace_id", workspaceId).eq("user_id", user.id),
      supabase.from("weekly_reports").select("week_label,status").eq("workspace_id", workspaceId).eq("user_id", user.id).order("week_start", { ascending: false }),
      supabase.from("approval_docs").select("id", { count: "exact" }).eq("workspace_id", workspaceId).eq("user_id", user.id),
      supabase.from("event_logs").select("id", { count: "exact" }).eq("workspace_id", workspaceId).eq("user_id", user.id),
      supabase.from("daily_checkins").select("checkin_date").eq("workspace_id", workspaceId).eq("user_id", user.id).order("checkin_date", { ascending: false }),
      supabase.from("final_presentations").select("title,admin_score,admin_feedback,status").eq("workspace_id", workspaceId).eq("user_id", user.id).maybeSingle(),
      supabase.from("workspace_members").select("joined_at").eq("workspace_id", workspaceId).eq("user_id", user.id).maybeSingle(),
    ]);

    // 허브별 통계
    const hmap: Record<string, number> = {};
    (tasks ?? []).forEach((t: { hub: string }) => { hmap[t.hub] = (hmap[t.hub] ?? 0) + 1; });
    setHubStats(Object.entries(hmap).sort((a, b) => b[1] - a[1]).map(([hub, count]) => ({ hub, count })));
    setTotalTasks(tasks?.length ?? 0);

    setWeeklyReports((reports ?? []) as WeeklyReport[]);
    setApprovalCount(approvals?.length ?? 0);
    setEventCount(events?.length ?? 0);

    // 연속 출근
    const checkinDates = (checkins ?? []).map((c: { checkin_date: string }) => c.checkin_date);
    setTotalCheckins(checkinDates.length);
    let s = 0;
    const dateSet = new Set(checkinDates);
    const cur = new Date();
    const todayStr = cur.toISOString().slice(0, 10);
    if (!dateSet.has(todayStr)) cur.setDate(cur.getDate() - 1);
    while (dateSet.has(cur.toISOString().slice(0, 10))) { s++; cur.setDate(cur.getDate() - 1); }
    setStreakDays(s);

    setFinalPresentation(fp as FinalPresentation | null);
    setJoinedAt((member as { joined_at: string } | null)?.joined_at ?? null);
    setLoading(false);
  }, [user, workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rep = companyState?.reputation ?? 0;
  const capital = companyState?.capital ?? 0;

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <main className="flex-1 p-5 overflow-y-auto">
          {loading ? (
            <div className="space-y-4">{[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-white/[0.02] rounded-2xl animate-pulse border border-white/[0.04]" />)}</div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-5">
              {/* 헤더 카드 — 명함 스타일 */}
              <div className="relative overflow-hidden rounded-2xl border border-white/[0.08]"
                style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)" }}>
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 80% 50%, #4B6CF7 0%, transparent 60%)" }} />
                <div className="relative px-8 py-8">
                  <div className="flex items-start justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-14 h-14 rounded-xl overflow-hidden border border-white/10 bg-white/[0.05] flex items-center justify-center">
                          {profile?.avatar_url ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover object-top" /> : <i className="ri-user-3-line text-2xl text-white/20"></i>}
                        </div>
                        <div>
                          <h1 className="text-white text-xl font-bold">{profile?.display_name ?? "이름 없음"}</h1>
                          <p className="text-white/40 text-sm">{roleConfig.label} · {companyConfig.label}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="px-3 py-1 rounded-full text-xs font-bold border" style={{ color: getRankColor(rep), borderColor: `${getRankColor(rep)}40`, backgroundColor: `${getRankColor(rep)}15` }}>
                          {getRankName(rep)}
                        </span>
                        <span className="text-white/30 text-xs">{rep}pt</span>
                        {joinedAt && <span className="text-white/30 text-xs">{new Date(joinedAt).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric" })} 입사</span>}
                      </div>
                    </div>
                    {/* 핵심 수치 */}
                    <div className="grid grid-cols-2 gap-3 shrink-0">
                      {[
                        { label: "자본금", value: `${capital.toLocaleString()}만원`, color: "#00C896" },
                        { label: "직급점수", value: `${rep}pt`, color: "#FF9F43" },
                        { label: "태스크 완료", value: `${totalTasks}건`, color: "#7A9BFF" },
                        { label: "출근 일수", value: `${totalCheckins}일`, color: "#E8375F" },
                      ].map(s => (
                        <div key={s.label} className="bg-white/[0.06] rounded-xl border border-white/[0.08] px-4 py-2.5 text-center">
                          <div className="text-lg font-bold tabular-nums" style={{ color: s.color }}>{s.value}</div>
                          <div className="text-white/30 text-[9px]">{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* 직급 프로그레스 바 */}
                  <div className="mt-5">
                    <div className="flex justify-between text-[9px] text-white/30 mb-1.5">
                      <span>인턴 (0)</span><span>사원 (21)</span><span>대리 (41)</span><span>과장 (61)</span><span>부장 (81)</span>
                    </div>
                    <div className="w-full h-2 bg-white/[0.08] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${rep}%`, backgroundColor: getRankColor(rep) }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* 최종 발표 결과 (있는 경우) */}
              {finalPresentation?.status === "reviewed" && finalPresentation.admin_score && (
                <div className="bg-[#111112] rounded-2xl border border-[#00C896]/25 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <i className="ri-award-fill text-[#00C896] text-xl"></i>
                    <span className="text-[#F2F2F2] text-sm font-bold">최종 발표 강사 평가</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-5xl font-black" style={{ color: { 5:"#00C896",4:"#7A9BFF",3:"#FF9F43",2:"#E8375F",1:"#888" }[finalPresentation.admin_score] ?? "#888" }}>
                      {{ 5:"A+",4:"A",3:"B",2:"C",1:"D" }[finalPresentation.admin_score] ?? "?"}
                    </div>
                    <div className="flex-1">
                      <p className="text-[#CCCCCC] text-sm font-medium">{finalPresentation.title}</p>
                      {finalPresentation.admin_feedback && <p className="text-[#777777] text-xs mt-1 leading-relaxed">{finalPresentation.admin_feedback}</p>}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* 허브별 활동 */}
                <div className="bg-[#111112] rounded-2xl border border-white/[0.05] p-5">
                  <h3 className="text-[#F2F2F2] text-sm font-semibold mb-4 flex items-center gap-2">
                    <i className="ri-bar-chart-grouped-line text-[#7A9BFF] text-base"></i>허브별 태스크 완료
                  </h3>
                  {hubStats.length === 0 ? <p className="text-[#333333] text-xs">아직 완료한 태스크가 없어요</p> : (
                    <div className="space-y-2.5">
                      {hubStats.map((h, i) => (
                        <div key={h.hub}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[#AAAAAA] text-xs">{HUB_LABELS[h.hub] ?? h.hub}</span>
                            <span className="text-xs font-bold tabular-nums" style={{ color: HUB_COLORS[i % HUB_COLORS.length] }}>{h.count}건</span>
                          </div>
                          <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.min(100,(h.count/totalTasks)*100)}%`, backgroundColor: HUB_COLORS[i % HUB_COLORS.length] }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 활동 요약 */}
                <div className="bg-[#111112] rounded-2xl border border-white/[0.05] p-5 space-y-3">
                  <h3 className="text-[#F2F2F2] text-sm font-semibold mb-4 flex items-center gap-2">
                    <i className="ri-clipboard-line text-[#FF9F43] text-base"></i>활동 이력
                  </h3>
                  {[
                    { label: "전자결재 제출", value: approvalCount, unit: "건", color: "#FF9F43", icon: "ri-file-paper-2-line" },
                    { label: "이벤트 대응", value: eventCount, unit: "건", color: "#E8375F", icon: "ri-notification-3-line" },
                    { label: "주간 보고서", value: weeklyReports.length, unit: "건", color: "#00C896", icon: "ri-file-text-line" },
                    { label: "연속 출근", value: streakDays, unit: "일", color: "#7A9BFF", icon: "ri-fire-line" },
                  ].map(s => (
                    <div key={s.label} className="flex items-center justify-between px-3 py-2.5 bg-white/[0.02] rounded-xl border border-white/[0.04]">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg" style={{ backgroundColor: `${s.color}15` }}>
                          <i className={`${s.icon} text-sm`} style={{ color: s.color }}></i>
                        </div>
                        <span className="text-[#888888] text-xs">{s.label}</span>
                      </div>
                      <span className="text-sm font-bold tabular-nums" style={{ color: s.color }}>{s.value}<span className="text-[10px] ml-0.5 font-normal">{s.unit}</span></span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 주간 보고서 히스토리 */}
              {weeklyReports.length > 0 && (
                <div className="bg-[#111112] rounded-2xl border border-white/[0.05] p-5">
                  <h3 className="text-[#F2F2F2] text-sm font-semibold mb-4 flex items-center gap-2">
                    <i className="ri-calendar-check-line text-[#00C896] text-base"></i>주간 보고서 이력
                    <span className="text-[#444444] text-xs font-normal">({weeklyReports.filter(r => r.status === "approved").length}/{weeklyReports.length} 승인)</span>
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {weeklyReports.map(r => (
                      <div key={r.week_label} className="px-2.5 py-1.5 rounded-lg border text-[10px] font-medium"
                        style={r.status === "approved" ? { borderColor: "rgba(0,200,150,0.25)", backgroundColor: "rgba(0,200,150,0.08)", color: "#00C896" }
                          : r.status === "rejected" ? { borderColor: "rgba(232,55,95,0.25)", backgroundColor: "rgba(232,55,95,0.08)", color: "#E8375F" }
                          : { borderColor: "rgba(255,255,255,0.08)", color: "#555555" }}>
                        {r.week_label}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 포트폴리오 미제출 안내 */}
              {!finalPresentation && (
                <div className="bg-[#111112] rounded-2xl border border-[#7A9BFF]/20 p-5 flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#7A9BFF]/15 shrink-0">
                    <i className="ri-presentation-line text-[#7A9BFF] text-xl"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#CCCCCC] text-sm font-semibold">최종 발표 자료를 아직 제출하지 않았어요</p>
                    <p className="text-[#444444] text-xs mt-0.5">대시보드에서 최종 발표 자료를 작성하고 강사에게 제출하세요</p>
                  </div>
                  <button type="button" onClick={() => navigate("/dashboard")}
                    className="px-4 py-2 rounded-xl bg-[#7A9BFF] text-white text-xs font-semibold cursor-pointer hover:bg-[#8AAEFF] transition-colors whitespace-nowrap shrink-0">
                    작성하기
                  </button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
